import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import configData from '../../config.json';
import ChangePasswordModal from '../../components/Profile/ChangePasswordModal';
import './styles.css';
import ProfilePicture from '../../components/Profile/ProfilePicture';
import ProfileInformation from '../../components/Profile/ProfileInformation';
import { useDataContext } from '../../context/data';
import { useOrderContext } from '../../context/order';
import translations from '../../constants/translations.json';
import SubscriptionSection from '../../components/HeyWaitrPlus/SubscriptionSection';
import { fetchUserProfile } from '../../utils/heywaitrPlusApi';
import { carriedRefreshToken, withLatestTokens } from '../../utils/authSession';

const readStoredProfile = () => {
  try {
    return JSON.parse(localStorage.getItem('user_profile'));
  } catch (e) {
    return null;
  }
};

const Profile = () => {
  const [userProfile, setUserProfile] = useState(readStoredProfile);
  const [passwordmodal, setpasswordmodal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const subscriptionRef = useRef(null);

  const { language, setUserProfile: setContextUserProfile } = useDataContext();
  const orderContext = useOrderContext();

  // Route guard: /profile is for logged-in users only. If there's no stored
  // session token, send the user home and show the login modal.
  useEffect(() => {
    if (!readStoredProfile()?.token && !searchParams.get('token')) {
      navigate('/', { replace: true });
      orderContext?.setLoginModal?.(true);
    }
  }, []);

  // Scroll to the membership section on /profile#subscription, once it has loaded.
  const handleSubscriptionLoaded = useCallback(() => {
    if (hash === '#subscription') {
      subscriptionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash]);

  // Rehydrate the web session from the mobile app's `/profile?token=...` deep-link
  // (used to manage a HeyWaitr+ subscription), and surface checkout-return status.
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const subParam = searchParams.get('sub');
    if (!tokenParam && !subParam) return;

    let cancelled = false;
    const next = new URLSearchParams(searchParams);

    const run = async () => {
      if (tokenParam) {
        try {
          const profile = await fetchUserProfile(tokenParam);
          // Keep this user's refresh token; the deep link carries none.
          const merged = { token: tokenParam, refreshToken: carriedRefreshToken(tokenParam), profile };
          if (!cancelled) {
            localStorage.setItem('user_profile', JSON.stringify(merged));
            setUserProfile(merged);
            setContextUserProfile?.(merged);
          }
        } catch (e) {
          // Invalid/expired token — leave the existing session untouched.
        }
        next.delete('token');
      }
      if (subParam) {
        if (subParam === 'success') {
          toast.success(
            translations[language]?.heywaitrplus_subscribe_success || 'Welcome to heywaitr+!',
          );
        }
        next.delete('sub');
      }
      if (!cancelled) setSearchParams(next, { replace: true });
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePhoto = async (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      try {
        const response = await axios({
          method: 'POST',
          url: `${configData.SERVER_URL}/upload`,
          data: {
            filename: file.name,
            image: reader.result,
          },
          headers: {
            Authorization: `Bearer ${userProfile.token}`,
          },
          validateStatus: () => true,
        });

        handleChange('avatar', response?.data?.url);
      } catch (error) {
        console.error(error?.response?.data);
      }
    };
  };

  const handleChange = (key, value) => {
    const payload = {
      ...userProfile,
      profile: {
        ...(userProfile?.profile || {}),
        [key]: value,
      },
    };

    setUserProfile(payload);
  };

  const saveProfile = async () => {
    try {
      await axios({
        method: 'PUT',
        url: `${configData.SERVER_URL}/user/edit`,
        data: userProfile.profile,
        headers: {
          Authorization: `Bearer ${userProfile.token}`,
        },
      });

      toast.success(translations[language]?.profile_save_success_message);
      // State may hold a token pair that has since been rotated; keep the stored one.
      const saved = withLatestTokens(userProfile);
      window.localStorage.setItem('user_profile', JSON.stringify(saved));

      // Keep the app-wide DataContext in sync so Navbar/HeyWaitr+ reflect the edit
      // without a page reload (mirrors the deep-link path above).
      setContextUserProfile?.(saved);
    } catch (error) {
      console.error(error?.response?.data);
    }
  };

  const togglepasswordmodal = () => {
    setpasswordmodal(!passwordmodal);
  };

  return (
    <div className="max-w-[720px] mx-auto py-8 px-4">
      {/* Page header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-[#1a1a1a] mb-1">
          {translations[language]?.my_profile}
        </h2>
        <p className="text-sm text-gray-400">{translations[language]?.my_profile_subtitle}</p>
      </div>

      {/* Profile picture */}
      <ProfilePicture userProfile={userProfile} handleChangePhoto={handleChangePhoto} />

      {/* General information */}
      <div className="mt-4">
        <ProfileInformation
          userProfile={userProfile}
          handleChange={handleChange}
          saveProfile={saveProfile}
        />
      </div>

      {/* Change password */}
      <div className="bg-white rounded-2xl py-7 shadow-sm">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-1">
              {translations[language]?.profile_change_password}
            </h3>
            <p className="text-[13px] text-gray-400">
              {translations[language]?.profile_change_password_description}
            </p>
          </div>
          <button
            onClick={togglepasswordmodal}
            className="py-2.5 px-6 text-[13px] font-semibold bg-[#f5f5f5] text-[#333] border border-[#e0e0e0] rounded-[10px] cursor-pointer transition-all duration-200 whitespace-nowrap hover:border-primary hover:text-primary"
          >
            <i className="fas fa-lock mr-2" />
            {translations[language]?.profile_change_password}
          </button>
        </div>
      </div>

      {/* HeyWaitr+ membership */}
      {userProfile?.token && (
        <div id="subscription" ref={subscriptionRef}>
          <SubscriptionSection
            token={userProfile.token}
            initialSubscribed={userProfile?.profile?.isSubscribed}
            onLoaded={handleSubscriptionLoaded}
          />
        </div>
      )}

      <ChangePasswordModal
        open={passwordmodal}
        setpasswordmodal={setpasswordmodal}
        toggle={togglepasswordmodal}
        userProfile={userProfile}
      />
    </div>
  );
};

export default Profile;
